import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps, withFormik } from 'formik';
import classnames from 'classnames';
import { Button, Form } from 'semantic-ui-react';
import styled from 'styled-components';

import { Admin, adminFactory } from '@/types/admin';
import { adminSchema } from '@/services/validators/admin';
import { classifiedSort } from '@/utils/name';
import { Label } from '@/components/shared/Label';

type OuterProps = {
  admin: Admin;
  isLoading: boolean;
  onSubmit: (admin: Admin) => void;
};

type Values = {
  firstName: string;
  lastName: string;
  kanaFirstName: string;
  kanaLastName: string;
};

export const NameEditForm = withFormik<OuterProps, Values>({
  mapPropsToValues: ({ admin }: OuterProps) => ({
    firstName: admin?.firstName ?? '',
    lastName: admin?.lastName ?? '',
    kanaFirstName: admin?.kanaFirstName ?? '',
    kanaLastName: admin?.kanaLastName ?? '',
  }),
  validationSchema: adminSchema,
  handleSubmit: (values, { props, setSubmitting }) => {
    const admin = adminFactory({
      id: props.admin.id,
      firstName: values.firstName,
      lastName: values.lastName,
      kanaFirstName: values.kanaFirstName,
      kanaLastName: values.kanaLastName,
    });
    props.onSubmit(admin);
    setSubmitting(false);
  },
  enableReinitialize: true,
})(
  // enhanced form
  ({
    errors,
    handleChange,
    handleSubmit,
    isLoading,
    dirty,
    isSubmitting,
    touched,
    values,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();

    return (
      <Form onSubmit={handleSubmit} className="name-edit-form">
        <StyledLabel>{t('models.contact.personInCharge')}</StyledLabel>
        <Form.Group widths="equal">
          {classifiedSort([
            <Form.Field key="first-name">
              <Form.Input
                data-test="first-name"
                className={classnames('field', { error: errors.firstName })}
                fluid={true}
                key="first-name"
                placeholder={t('ui.expression.firstName')}
                name="firstName"
                onChange={handleChange}
                value={values.firstName}
              />
              {touched.firstName && errors.firstName && (
                <Label classNames={'parallel-elem'}>{errors.firstName}</Label>
              )}
            </Form.Field>,
            <Form.Field key="last-name">
              <Form.Input
                data-test="last-name"
                className={classnames('field', { error: errors.lastName })}
                fluid={true}
                key="last-name"
                placeholder={t('ui.expression.lastName')}
                name="lastName"
                onChange={handleChange}
                value={values.lastName}
              />
              {touched.lastName && errors.lastName && (
                <Label classNames={'parallel-elem'}>{errors.lastName}</Label>
              )}
            </Form.Field>,
          ])}
        </Form.Group>

        <StyledLabel>
          {t('models.contact.personInCharge')}（{t('ui.expression.phonetic')}）
        </StyledLabel>
        <Form.Group widths="equal">
          {classifiedSort([
            <Form.Field key="kana-first-name">
              <Form.Input
                data-test="kana-first-name"
                className={classnames('field', { error: errors.kanaFirstName })}
                fluid={true}
                placeholder={t('ui.expression.firstNamePhonetic')}
                name="kanaFirstName"
                onChange={handleChange}
                value={values.kanaFirstName}
              />
              {touched.kanaFirstName && errors.kanaFirstName && (
                <Label classNames={'parallel-elem'}>
                  {errors.kanaFirstName}
                </Label>
              )}
            </Form.Field>,
            <Form.Field key="kana-last-name">
              <Form.Input
                data-test="kana-last-name"
                className={classnames('field', { error: errors.kanaLastName })}
                fluid={true}
                placeholder={t('ui.expression.lastNamePhonetic')}
                name="kanaLastName"
                onChange={handleChange}
                value={values.kanaLastName}
              />
              {touched.kanaLastName && errors.kanaLastName && (
                <Label classNames={'parallel-elem'}>
                  {errors.kanaLastName}
                </Label>
              )}
            </Form.Field>,
          ])}
        </Form.Group>

        <StyledSubmitButton>
          <Button
            data-test="update-password"
            disabled={!dirty || isSubmitting || isLoading}
            primary={true}
            size="large"
            type="submit"
          >
            {t('ui.action.changeNow')}
          </Button>
        </StyledSubmitButton>
      </Form>
    );
  },
);

const StyledLabel = styled.label`
  display: block;
  margin-bottom: 8px !important;
`;

const StyledSubmitButton = styled.div`
  color: #bbb;
  text-align: center;
  margin-top: 3.2em;
`;
